/**
 * Energy Profile Generators (country-aware with Czech fallback defaults).
 *
 * Originally based on Czech Republic (Zlin Region) data:
 *   docs/RESEARCH_2026-02-09_czech_zlin_energy_data.md
 *
 * Now uses COUNTRY_DEFAULTS for per-country adaptation.
 */

/**
 * Derive the meteorological season from a date.
 * @param {Date|string} [date] - Date to determine season from (defaults to today)
 * @returns {'winter'|'spring'|'summer'|'fall'}
 */
export function getSeason(date) {
  const d = date ? new Date(date) : new Date()
  const month = d.getMonth() // 0-indexed
  if (month >= 2 && month <= 4) return 'spring'   // Mar-May
  if (month >= 5 && month <= 7) return 'summer'   // Jun-Aug
  if (month >= 8 && month <= 10) return 'fall'    // Sep-Nov
  return 'winter'                                  // Dec-Feb
}

// ---------------------------------------------------------------------------
// Helper: deterministic pseudo-random (avoids Math.random for consistency)
// ---------------------------------------------------------------------------
function seededRng(seed) {
  let s = Math.abs(seed) || 1
  return () => {
    s = (s * 16807 + 12345) % 2147483647
    return (s & 0x7fffffff) / 2147483647
  }
}

// ---------------------------------------------------------------------------
// 1. Community Load Profile  (kW per hour, community aggregate)
// ---------------------------------------------------------------------------

/**
 * Country-specific hourly load weight factors (24 values summing to ~1.0).
 *
 * Each profile reflects the typical mixed-community load shape for that country:
 * - Czech Republic / Germany: strong evening peak (18-19h), moderate morning ramp, low overnight
 * - Italy / Greece: afternoon dip (siesta), later evening peak (20-21h), summer AC component
 * - France: double peak (morning heating + evening), strong winter evening from electric heating
 */
const COUNTRY_LOAD_WEIGHTS = {
  'Czech Republic': [
    0.024, 0.022, 0.020, 0.019, 0.019, 0.021, // 0-5   night
    0.032, 0.045, 0.055, 0.054, 0.052, 0.050,  // 6-11  morning + office
    0.048, 0.046, 0.047, 0.049, 0.051, 0.058,  // 12-17 afternoon
    0.066, 0.074, 0.068, 0.055, 0.042, 0.033,  // 18-23 evening peak
  ],
  'Germany': [
    0.023, 0.021, 0.020, 0.019, 0.019, 0.022, // 0-5   low overnight
    0.035, 0.048, 0.056, 0.055, 0.053, 0.050,  // 6-11  early morning ramp, office plateau
    0.048, 0.046, 0.046, 0.048, 0.052, 0.060,  // 12-17 steady afternoon
    0.068, 0.072, 0.065, 0.052, 0.040, 0.032,  // 18-23 strong evening peak at 19h
  ],
  'Italy': [
    0.026, 0.024, 0.022, 0.020, 0.020, 0.022, // 0-5   warm overnight base (AC)
    0.030, 0.040, 0.050, 0.054, 0.056, 0.055,  // 6-11  gradual morning ramp
    0.052, 0.044, 0.040, 0.042, 0.048, 0.054,  // 12-17 afternoon dip (siesta 13-15h)
    0.060, 0.066, 0.070, 0.062, 0.048, 0.035,  // 18-23 late evening peak at 20h
  ],
  'Greece': [
    0.028, 0.026, 0.024, 0.022, 0.021, 0.022, // 0-5   higher overnight (AC/warm climate)
    0.028, 0.036, 0.046, 0.052, 0.056, 0.055,  // 6-11  later morning start
    0.050, 0.042, 0.038, 0.040, 0.046, 0.052,  // 12-17 midday dip (siesta 13-15h)
    0.058, 0.064, 0.068, 0.065, 0.050, 0.035,  // 18-23 late evening peak at 20h
  ],
  'France': [
    0.026, 0.024, 0.022, 0.020, 0.020, 0.023, // 0-5   moderate overnight (electric heating)
    0.038, 0.052, 0.058, 0.054, 0.050, 0.048,  // 6-11  strong morning peak (heating + cooking)
    0.046, 0.044, 0.044, 0.046, 0.050, 0.056,  // 12-17 steady afternoon
    0.064, 0.070, 0.066, 0.055, 0.042, 0.032,  // 18-23 evening peak at 19h (cooking + heating)
  ],
}

/** Resolve country name variants to canonical key */
function resolveCountryForLoad(country) {
  if (!country) return 'Czech Republic'
  const c = country.toLowerCase()
  if (c.includes('czech')) return 'Czech Republic'
  if (c.includes('german')) return 'Germany'
  if (c.includes('ital')) return 'Italy'
  if (c.includes('gree') || c.includes('hellen')) return 'Greece'
  if (c.includes('fran')) return 'France'
  return 'Czech Republic' // default fallback
}

/**
 * Generate a 24-hour community load profile (country-aware).
 *
 * Shape adapts to country-specific consumption patterns:
 * - Central Europe (CZ/DE): strong evening peak, moderate morning
 * - Mediterranean (IT/GR): afternoon siesta dip, later evening peak, higher overnight (AC)
 * - France: double peak (morning heating + evening), electric heating influence
 *
 * @param {number} dailyKwh  - Total daily community consumption in kWh
 * @param {number} seed      - Deterministic seed
 * @param {string} [country] - Country name (e.g., 'Germany', 'Italy'). Defaults to Czech Republic.
 * @returns {number[]}         24-element array of kW values
 */
export function czechLoadProfile(dailyKwh = 178, seed = 42, country = null) {
  const rng = seededRng(seed)
  const key = resolveCountryForLoad(country)
  const w = COUNTRY_LOAD_WEIGHTS[key]
  const wSum = w.reduce((a, b) => a + b, 0)

  return w.map(wi => {
    const base = dailyKwh * (wi / wSum)
    const noise = (rng() - 0.5) * base * 0.06
    return Math.max(0.1, +(base + noise).toFixed(2))
  })
}

// ---------------------------------------------------------------------------
// 2. PV Generation Profile  (kW per hour)
// ---------------------------------------------------------------------------
/**
 * Generate a 24-hour PV generation profile (latitude-aware).
 *
 * Sunrise/sunset times and peak capacity factor are adjusted for latitude.
 * Higher latitudes (e.g. 55°N) → shorter winter days; lower latitudes (e.g. 37°N) → more even.
 *
 * @param {number} capacityKwp - Total installed PV (kWp)
 * @param {'winter'|'spring'|'summer'|'fall'} season
 * @param {number} seed
 * @param {number} latitude - Site latitude in degrees (default 47 = Central Europe)
 * @returns {number[]}  24-element array of kW values
 */
export function czechPVProfile(capacityKwp = 42, season = 'spring', seed = 43, latitude = 47) {
  const rng = seededRng(seed)

  // Latitude adjustment: each degree from 47°N shifts sunrise/sunset by ~2 min per season
  const latOffset = (latitude - 47) * 0.04  // hours shift per degree

  // Base season params for 47°N (Central European average)
  const baseSeasons = {
    winter:  { peakCF: 0.35, rise: 8.0, set: 16.5, noon: 11.75 },
    spring:  { peakCF: 0.40, rise: 5.5, set: 19.5, noon: 12.50 },
    summer:  { peakCF: 0.40, rise: 4.5, set: 21.0, noon: 12.75 },
    fall:    { peakCF: 0.33, rise: 6.5, set: 18.0, noon: 12.25 },
  }
  const base = baseSeasons[season] || baseSeasons.spring

  // Southern latitudes: earlier sunrise, later sunset, slightly higher CF
  // Northern latitudes: later sunrise, earlier sunset, slightly lower CF in winter
  const rise = base.rise + latOffset
  const set = base.set - latOffset
  const noon = base.noon
  const peakCF = base.peakCF + (47 - latitude) * 0.003  // +0.3% per degree south
  const sigma = (set - rise) / 4.5

  return Array.from({ length: 24 }, (_, h) => {
    if (h < rise || h > set) return 0
    const raw = peakCF * Math.exp(-0.5 * ((h - noon) / sigma) ** 2)
    const noise = (rng() - 0.5) * raw * 0.05
    return Math.max(0, +(capacityKwp * (raw + noise)).toFixed(2))
  })
}

// ---------------------------------------------------------------------------
// 3. Full Energy Balance  (load, pv, battery, grid)
// ---------------------------------------------------------------------------
/**
 * @param {object} params
 * @param {number} params.dailyKwh       - Community daily load (kWh)
 * @param {number} params.pvKwp          - PV capacity (kWp)
 * @param {number} params.batteryKwh     - Battery capacity (kWh)
 * @param {string} params.season         - 'winter'|'spring'|'summer'|'fall'
 * @param {number} params.seed
 * @returns {object} { hours, load, pv, wind, gridImport, gridExport, batteryCharge, batteryDischarge }
 */
export function czechEnergyBalance(params = {}) {
  const {
    dailyKwh = 178,
    pvKwp = 42,
    batteryKwh = 50,
    season = 'spring',
    seed = 42,
    country = null,
    latitude = 47,
  } = params

  const hours = Array.from({ length: 24 }, (_, i) => i)
  const load = czechLoadProfile(dailyKwh, seed, country)
  const pv = czechPVProfile(pvKwp, season, seed + 1, latitude)
  const wind = hours.map(() => 0) // No wind for Zlin community

  const maxPower = batteryKwh * 0.5
  let soc = batteryKwh * 0.40
  const batteryCharge = []
  const batteryDischarge = []
  const gridImport = []
  const gridExport = []

  for (let h = 0; h < 24; h++) {
    const net = pv[h] + wind[h] - load[h]
    let ch = 0, dis = 0, imp = 0, exp = 0

    if (net > 0) {
      // Surplus: charge battery, then export
      const room = Math.max(0, batteryKwh * 0.90 - soc)
      ch = Math.min(net, maxPower, room)
      soc += ch * 0.94
      exp = Math.max(0, net - ch)
    } else {
      // Deficit: discharge battery, then import
      const deficit = Math.abs(net)
      const avail = Math.max(0, soc - batteryKwh * 0.10)
      dis = Math.min(deficit, maxPower, avail)
      soc -= dis / 0.94
      imp = Math.max(0, deficit - dis * 0.94)
    }

    batteryCharge.push(+ch.toFixed(2))
    batteryDischarge.push(+dis.toFixed(2))
    gridImport.push(+imp.toFixed(2))
    gridExport.push(+exp.toFixed(2))
  }

  return { hours, load, pv, wind, gridImport, gridExport, batteryCharge, batteryDischarge }
}

// ---------------------------------------------------------------------------
// 4. Tariff Profile (currency-aware, falls back to country-specific patterns)
// ---------------------------------------------------------------------------
/**
 * Generate a time-of-use tariff profile for a community.
 * Uses community metadata tariffs when available, otherwise falls back
 * to country-specific patterns.
 *
 * @param {object} [community] - Community object with currency/metadata
 * @returns {object} { hours, importTariff, exportTariff, currency }
 */
export function czechTariffProfile(community) {
  const hours = Array.from({ length: 24 }, (_, i) => i)
  const currency = community?.currency || 'CZK'
  const isCZK = currency === 'CZK'
  const meta = community?.metadata || {}

  // Use country-specific defaults when no community metadata tariffs exist
  const countryRef = getCountryDefaults(community)
  let importTariff, exportTariff

  if (isCZK) {
    // Czech D02d two-tier tariff structure (CZK/kWh)
    const baseImport = meta.import_tariff || countryRef.avgTariff
    importTariff = hours.map(h => {
      if (h >= 22 || h < 6)   return +(baseImport * 0.69).toFixed(2)  // Low (night)
      if (h >= 6 && h < 7)    return +(baseImport * 0.82).toFixed(2)  // Transition
      if (h >= 7 && h < 9)    return +(baseImport * 1.13).toFixed(2)  // Morning peak
      if (h >= 9 && h < 17)   return +(baseImport * 1.00).toFixed(2)  // Day
      if (h >= 17 && h < 21)  return +(baseImport * 1.24).toFixed(2)  // Evening peak
      return +(baseImport * 0.91).toFixed(2)                           // Wind-down
    })
    const baseExport = meta.export_tariff || (countryRef.avgTariff * 0.35)
    exportTariff = hours.map(h => {
      if (h >= 10 && h < 15)  return +(baseExport * 0.90).toFixed(2)  // Solar glut
      if (h >= 17 && h < 21)  return +(baseExport * 1.20).toFixed(2)  // Demand premium
      return +baseExport.toFixed(2)
    })
  } else {
    // EUR tariff: time-of-use pattern with configurable base rates
    const baseImport = meta.import_tariff || countryRef.avgTariff
    importTariff = hours.map(h => {
      if (h >= 22 || h < 6)   return +(baseImport * 0.75).toFixed(4)  // Off-peak
      if ((h >= 7 && h <= 9) || (h >= 17 && h <= 21)) return +(baseImport * 1.20).toFixed(4) // Peak
      return +baseImport.toFixed(4)                                    // Standard
    })
    const baseExport = meta.export_tariff || (countryRef.avgTariff * 0.35)
    exportTariff = hours.map(h => {
      if (h >= 10 && h < 15)  return +(baseExport * 0.85).toFixed(4)  // Solar surplus
      if (h >= 17 && h < 21)  return +(baseExport * 1.15).toFixed(4)  // Demand premium
      return +baseExport.toFixed(4)
    })
  }

  return { hours, importTariff, exportTariff, currency }
}

// ---------------------------------------------------------------------------
// 5. Monthly Energy Bills (currency-aware)
// ---------------------------------------------------------------------------
/**
 * @param {Array<{label:string, annualKwh:number}>} buildings
 * @param {object} [opts] - { avgTariff, selfSufficiencyPct, currency }
 * @returns {object} { months, buildings: [{ name, data }], currency }
 */
export function czechMonthlyBills(buildings, opts = {}) {
  const months = opts.months || ['Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb']
  // Seasonal cost multipliers — configurable per climate zone
  // Default: Central European heating-degree-day distribution (winter peak ~1.38x)
  // Mediterranean: milder winters → flatter distribution
  const seasonal = opts.seasonalFactors || [0.88, 1.05, 1.28, 1.38, 1.32, 1.15]
  // Use caller-provided tariff; no hardcoded fallback — caller must provide via countryRef
  const avgTariff = opts.avgTariff || 0.20  // Generic fallback, caller should always provide
  const ssrPct = opts.selfSufficiencyPct || 53
  const gridFactor = 1 - ssrPct / 100  // Fraction of load met by grid

  const buildingsData = buildings.map(b => {
    const monthlyBaseKwh = (b.annualKwh || 3500) / 12
    const data = seasonal.map(factor => {
      const cost = monthlyBaseKwh * avgTariff * factor * gridFactor
      return Math.round(cost)
    })
    return { name: b.label, data }
  })

  return { months, buildings: buildingsData, currency: opts.currency || 'EUR' }
}

// ---------------------------------------------------------------------------
// 6. Battery Schedule (SoC + charge/discharge for optimisation page)
// ---------------------------------------------------------------------------
/**
 * @param {number} batteryKwh
 * @param {number} seed
 * @returns {object} { soc, charge, discharge }
 */
export function czechBatterySchedule(batteryKwh = 50, seed = 42) {
  const rng = seededRng(seed + 100)
  const maxPower = batteryKwh * 0.5

  // SoC pattern: discharge overnight, charge during PV peak, discharge evening
  const soc = [
    42, 38, 34, 30, 27, 24, 22, 20, 18, // 0-8: slow overnight discharge
    22, 30, 42, 58, 72, 82, 88, 86, 84,  // 9-17: charge during PV
    78, 68, 56, 48, 44, 42,              // 18-23: evening discharge
  ].map(v => Math.min(90, Math.max(10, v + (rng() - 0.5) * 3)))

  const charge = Array.from({ length: 24 }, (_, h) => {
    if (h >= 9 && h <= 15) return +(maxPower * (0.6 + rng() * 0.3)).toFixed(1)
    return 0
  })

  const discharge = Array.from({ length: 24 }, (_, h) => {
    if (h >= 18 && h <= 22) return +(maxPower * (0.4 + rng() * 0.3)).toFixed(1)
    if (h >= 0 && h <= 5) return +(maxPower * (0.1 + rng() * 0.15)).toFixed(1)
    return 0
  })

  return { soc, charge, discharge }
}

// ---------------------------------------------------------------------------
// 7. Optimisation KPIs (consistent with communityKPIs dashboard values)
// ---------------------------------------------------------------------------
/**
 * Baseline values MUST match what communityKPIs() produces for the dashboard.
 * Optimized values show the improvement from day-ahead scheduling.
 *
 * @param {object} params
 * @param {number} params.dailyKwh    - Community daily load (kWh)
 * @param {number} params.pvKwp       - PV capacity (kWp)
 * @param {string} params.currency    - 'CZK' or 'EUR'
 * @returns {object} { baselineKPIs, optimizedKPIs }
 */
export function czechOptimizationKPIs(params = {}) {
  const { dailyKwh = 178, pvKwp = 42, currency = 'CZK', baselineSCR: passedSCR } = params
  const isCZK = currency === 'CZK'
  const rate = isCZK ? 1 : 1 / 24.39

  const dailyPvKwh = pvKwp * 1000 / 365

  // Baseline: unoptimized (matches communityKPIs dashboard formula)
  // Use SCR passed from communityKPIs if available, otherwise default to 82
  const baselineSCR = passedSCR || 82 // % of PV self-consumed (no smart scheduling, with battery buffer)
  const baselineExportKwh = dailyPvKwh * (1 - baselineSCR / 100)
  const baselineSelfConsumedKwh = dailyPvKwh * baselineSCR / 100
  const baselineGridImportKwh = Math.max(0, dailyKwh - baselineSelfConsumedKwh)
  const baselineSSR = Math.round(baselineSelfConsumedKwh / dailyKwh * 100)

  // Optimized: smart battery + flex load scheduling captures nearly all PV
  // SCR 97% = battery flex + load shifting captures nearly all PV, ~3% curtailment
  // SSR is derived: 0.97 * dailyPvKwh / dailyKwh = ~63%
  const optSCR = 97 // % of PV self-consumed (optimized scheduling)
  const optExportKwh = dailyPvKwh * (1 - optSCR / 100)
  const optSelfConsumedKwh = dailyPvKwh * optSCR / 100
  const optGridImportKwh = Math.max(0, dailyKwh - optSelfConsumedKwh)
  const optSSR = Math.round(optSelfConsumedKwh / dailyKwh * 100)

  // Battery arbitrage: 14 kWh charged from cheap overnight grid, discharged to export at premium evening hours
  // Physically plausible: 50 kWh battery * 0.8 usable * ~35% allocated to arbitrage
  const batteryArbitrageKwh = 14

  return {
    baselineKPIs: {
      daily_cost_eur: +(baselineGridImportKwh * 5.50 * rate).toFixed(1),
      self_sufficiency_pct: baselineSSR,
      self_consumption_pct: baselineSCR,
      grid_import_kwh: +baselineGridImportKwh.toFixed(0),
      grid_export_revenue_eur: +(baselineExportKwh * 1.75 * rate).toFixed(1), // mostly midday solar glut prices
      co2_avoided_kg: 0,
    },
    optimizedKPIs: {
      daily_cost_eur: +(optGridImportKwh * 5.50 * rate).toFixed(1),
      self_sufficiency_pct: optSSR,
      self_consumption_pct: optSCR,
      grid_import_kwh: +optGridImportKwh.toFixed(0),
      grid_export_revenue_eur: +((optExportKwh * 2.35 + batteryArbitrageKwh * 2.40) * rate).toFixed(1), // premium timing + battery arbitrage
      co2_avoided_kg: +((baselineGridImportKwh - optGridImportKwh) * 0.4).toFixed(1),
      peak_import_kw: +(optGridImportKwh / 10).toFixed(1),
      grid_import_cost_eur: +(optGridImportKwh * 5.50 * rate).toFixed(1),
    },
  }
}

// ---------------------------------------------------------------------------
// 8. Flex Load Schedules (for Gantt chart)
// ---------------------------------------------------------------------------
/**
 * @param {Array} buildings - community buildings with assets
 * @returns {Array<{building_id, asset_name, asset_type, hours_on}>}
 */
export function czechFlexLoadSchedules(buildings = []) {
  const schedules = []

  buildings.forEach(b => {
    (b.building_assets || []).forEach(a => {
      if (a.asset_type === 'heat_pump') {
        schedules.push({
          building_id: b.building_id,
          asset_name: 'Heat Pump',
          asset_type: 'heat_pump',
          hours_on: Array.from({ length: 24 }, (_, h) =>
            (h >= 0 && h <= 5) || (h >= 10 && h <= 14)
          ),
        })
      }
      if (a.asset_type === 'ev_charger') {
        schedules.push({
          building_id: b.building_id,
          asset_name: 'EV Charger',
          asset_type: 'ev_charger',
          hours_on: Array.from({ length: 24 }, (_, h) =>
            (h >= 0 && h <= 5) || h === 23
          ),
        })
      }
      if (a.asset_type === 'hvac') {
        schedules.push({
          building_id: b.building_id,
          asset_name: 'HVAC',
          asset_type: 'hvac',
          hours_on: Array.from({ length: 24 }, (_, h) =>
            (h >= 8 && h <= 11) || (h >= 14 && h <= 17)
          ),
        })
      }
    })
  })

  // If no flex loads found, return a default set
  if (schedules.length === 0) {
    return [
      {
        building_id: 'Residence G',
        asset_name: 'Heat Pump',
        asset_type: 'heat_pump',
        hours_on: Array.from({ length: 24 }, (_, h) =>
          (h >= 0 && h <= 5) || (h >= 10 && h <= 14)
        ),
      },
      {
        building_id: 'Residence H',
        asset_name: 'EV Charger',
        asset_type: 'ev_charger',
        hours_on: Array.from({ length: 24 }, (_, h) =>
          (h >= 0 && h <= 5) || h === 23
        ),
      },
    ]
  }

  return schedules
}

// ---------------------------------------------------------------------------
// 9. CBA Results (CZK-based for Czech community)
// ---------------------------------------------------------------------------
/**
 * @param {object} params
 * @param {number} params.years - analysis period
 * @param {string} params.currency
 * @returns {object} Complete CBA results
 */
export function czechCBAResults(params = {}) {
  const { years = 20, currency = 'CZK' } = params
  const isCZK = currency === 'CZK'
  const r = isCZK ? 1 : 1 / 24.39

  // Community parameters:
  //   Annual load: 65,000 kWh | PV: 42 kWp (42,000 kWh/yr) | Battery: 50 kWh
  //   No-intervention grid cost: 65,000 * 5.50 = 357,500 CZK/yr
  //   PV Only (SCR ~70% without battery): self-consumed 29,400 kWh, import 35,600, export 12,600
  //     Grid savings: 29,400 * 5.50 = 161,700; Export: 12,600 * 1.75 = 22,050
  //     Annual savings: 183,750 CZK; O&M: 15,000 CZK; Net: 168,750 CZK
  //   PV + BESS (SCR ~82%): self-consumed 34,440 kWh, import 30,560, export 7,560
  //     Grid savings: 34,440 * 5.50 = 189,420; Export: 7,560 * 1.75 = 13,230
  //     Annual savings: 202,650 CZK; O&M: 30,000 CZK; Net: 172,650 CZK
  //   CAPEX PV: 750,000 CZK | CAPEX PV+BESS: 1,450,000 CZK

  return {
    scenarios: [
      {
        name: 'No Intervention',
        npv: 0,
        bcr: 1.0,
        payback: null,
        annualSavings: 0,
        co2Avoided: 0,
      },
      {
        name: 'Community PV Only',
        npv: Math.round(1090000 * r),
        bcr: 2.5,
        payback: 4.5,
        annualSavings: Math.round(168750 * r),
        co2Avoided: 11.8,  // 29,400 kWh * 0.4 kg/kWh / 1000
      },
      {
        name: 'PV + Battery (BESS)',
        npv: Math.round(550000 * r),
        bcr: 1.4,
        payback: 8.4,
        annualSavings: Math.round(172650 * r),
        co2Avoided: 16.3,  // (65000 - grid_import_optimized) * 0.4 / 1000
      },
    ],
    cumulativeCashFlow: {
      years: Array.from({ length: years + 1 }, (_, i) => i),
      baseline: Array(years + 1).fill(0),
      pvOnly: Array.from({ length: years + 1 }, (_, i) => {
        const capex = -750000 * r
        const annualBenefit = 168750 * r
        return Math.round(capex + annualBenefit * i)
      }),
      pvBess: Array.from({ length: years + 1 }, (_, i) => {
        const capex = -1450000 * r
        const annualBenefit = 172650 * r
        return Math.round(capex + annualBenefit * i)
      }),
    },
    annualCostBreakdown: {
      categories: ['Year 1', 'Year 5', 'Year 10', 'Year 15', 'Year 20'],
      noIntervention: {
        // Grid cost: 357,500 CZK base, growing ~2% p.a. with inflation
        gridImport: [357500, 387300, 427600, 472100, 521200].map(v => Math.round(v * r)),
        pvValue: [0, 0, 0, 0, 0],
        batteryValue: [0, 0, 0, 0, 0],
        om: [0, 0, 0, 0, 0],
      },
      pvOnly: {
        // Reduced grid import + PV generation value + O&M
        gridImport: [195800, 212100, 234200, 258600, 285500].map(v => Math.round(v * r)),
        pvValue: [161700, 175200, 193400, 213500, 235700].map(v => Math.round(v * r)),
        batteryValue: [0, 0, 0, 0, 0],
        om: [15000, 16500, 18200, 20100, 22200].map(v => Math.round(v * r)),
      },
      pvBess: {
        // Further reduced import + higher PV value (more self-consumed) + battery value + higher O&M
        gridImport: [168100, 182100, 201000, 221800, 244900].map(v => Math.round(v * r)),
        pvValue: [189420, 205200, 226500, 250100, 276100].map(v => Math.round(v * r)),
        batteryValue: [36200, 39200, 43300, 47800, 52800].map(v => Math.round(v * r)),
        om: [30000, 33000, 36400, 40200, 44400].map(v => Math.round(v * r)),
      },
    },
    sensitivity: {
      discountRates: [5, 6, 7, 8, 9, 10],
      noIntervention: [0, 0, 0, 0, 0, 0],
      pvOnly: [1310000, 1200000, 1090000, 1000000, 920000, 850000].map(v => Math.round(v * r)),
      pvBess: [780000, 660000, 550000, 470000, 390000, 320000].map(v => Math.round(v * r)),
    },
  }
}

// ---------------------------------------------------------------------------
// 10. Long-Term Results
// ---------------------------------------------------------------------------
export function czechLongTermResults(params = {}) {
  const { numYears = 5, currency = 'CZK' } = params
  const r = currency === 'CZK' ? 1 : 1 / 24.39

  const years = Array.from({ length: numYears + 1 }, (_, i) => `Year ${i}`)

  // Starting values match communityKPIs dashboard: SSR = 53%, SCR = 82%
  // Improvement over time from learning, degradation management, flex load adoption
  // SSR grows from 53% toward ~68% over 5 years (realistic with smart scheduling adoption)
  const selfSufficiency = Array.from({ length: numYears + 1 }, (_, i) =>
    Math.min(80, +(53 + 3.0 * i).toFixed(1))
  )
  // SCR grows from 82% toward ~95% over 5 years (battery + flex load optimization)
  const selfConsumption = Array.from({ length: numYears + 1 }, (_, i) =>
    Math.min(98, +(82 + 2.5 * i).toFixed(1))
  )
  const gridDependence = selfSufficiency.map(ss => +(100 - ss).toFixed(1))

  // Annual savings: baseline grid cost ~168k CZK/yr (SSR 53%), improving over time
  const cumulativeSavings = []
  for (let i = 0; i <= numYears; i++) {
    if (i === 0) { cumulativeSavings.push(0); continue }
    const annual = Math.round((190000 + i * 10000) * r) // CZK/yr (from PV self-consumption + optimization)
    cumulativeSavings.push(cumulativeSavings[i - 1] + annual)
  }

  // CAPEX for PV + BESS: ~1,450,000 CZK
  const totalCapex = 1450000

  return {
    years,
    selfSufficiency,
    selfConsumption,
    gridDependence,
    cumulativeSavings,
    monthlyUtilization: {
      months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      pvUtilization: [48, 55, 65, 73, 80, 86, 89, 87, 77, 65, 53, 46],
      batteryCycles: [16, 18, 22, 26, 30, 33, 36, 35, 29, 24, 19, 17],
      flexLoadHours: [185, 170, 158, 148, 138, 130, 127, 132, 142, 155, 172, 188],
    },
    summary: {
      totalSavings: cumulativeSavings[numYears],
      avgSelfSufficiency: +(selfSufficiency.reduce((a, b) => a + b, 0) / selfSufficiency.length).toFixed(1),
      totalCO2Avoided: +(numYears * 16.3).toFixed(0),
      roi: +((cumulativeSavings[numYears] / (totalCapex * r)) * 100).toFixed(1),
    },
  }
}

// ---------------------------------------------------------------------------
// 11. KPI Summary (used by dashboard)
// ---------------------------------------------------------------------------
/**
 * Derive dashboard KPIs from community data.
 * @param {object} community - Full community object
 * @returns {object} { selfConsumption, selfSufficiency, dailyCost, yearlyCost, currency }
 */
// Country-specific reference values for parametric KPI estimation.
// Used as fallback when no real optimizer results are available.
export const COUNTRY_DEFAULTS = {
  'Czech Republic': { residentialKwhPerUnit: 3500, municipalKwh: 24000, commercialKwh: 40000, pvYieldKwhPerKwp: 1000, avgTariff: 5.50, tariffCurrency: 'CZK' },
  'Italy':          { residentialKwhPerUnit: 2700, municipalKwh: 28000, commercialKwh: 45000, pvYieldKwhPerKwp: 1300, avgTariff: 0.22, tariffCurrency: 'EUR' },
  'Germany':        { residentialKwhPerUnit: 4000, municipalKwh: 30000, commercialKwh: 50000, pvYieldKwhPerKwp: 1000, avgTariff: 0.32, tariffCurrency: 'EUR' },
  'Greece':         { residentialKwhPerUnit: 3200, municipalKwh: 26000, commercialKwh: 42000, pvYieldKwhPerKwp: 1500, avgTariff: 0.18, tariffCurrency: 'EUR' },
  'Spain':          { residentialKwhPerUnit: 3000, municipalKwh: 27000, commercialKwh: 44000, pvYieldKwhPerKwp: 1450, avgTariff: 0.17, tariffCurrency: 'EUR' },
  'France':         { residentialKwhPerUnit: 4700, municipalKwh: 32000, commercialKwh: 48000, pvYieldKwhPerKwp: 1150, avgTariff: 0.21, tariffCurrency: 'EUR' },
  '_default':       { residentialKwhPerUnit: 3500, municipalKwh: 26000, commercialKwh: 42000, pvYieldKwhPerKwp: 1100, avgTariff: 0.20, tariffCurrency: 'EUR' },
}

export function getCountryDefaults(community) {
  const country = community?.metadata?.country || ''
  return COUNTRY_DEFAULTS[country] || COUNTRY_DEFAULTS['_default']
}

/**
 * Estimate a single building's annual energy consumption (kWh/year).
 * Used by ForecastingPage and ModellingPage when no dataset is available.
 * Priority: 1) explicit metadata 2) floor_area × energy_class 3) country defaults by type+units
 */
export function estimateBuildingAnnualKwh(building, community) {
  const meta = building.metadata || {}

  // Priority 1: Explicit annual load from any metadata variant
  const explicit = meta.annual_load_kwh || meta.annual_consumption_kwh || meta.annual_kwh_approx
  if (explicit && explicit > 0) return explicit

  // Priority 2: Country defaults by building type + units
  // This is preferred over floor_area × energy_class because the EU energy intensity
  // benchmarks cover total energy (heating+cooling), not just electricity consumption.
  const countryRef = getCountryDefaults(community)
  const units = meta.num_units || 1
  const bType = building.building_type || 'residential'
  if (bType === 'commercial') return countryRef.commercialKwh || 25000
  if (bType === 'municipal' || bType === 'public') return countryRef.municipalKwh || 24000
  if (bType === 'industrial') return countryRef.commercialKwh || 150000
  return (countryRef.residentialKwhPerUnit || 3500) * units
}

export function communityKPIs(community) {
  if (!community) return null

  const currency = community.currency || 'EUR'
  const isCZK = currency === 'CZK'
  const buildings = community.buildings || []
  const assets = community.community_assets || []
  const countryRef = getCountryDefaults(community)

  // Total annual load: prefer real metadata over country defaults
  const totalAnnualKwh = buildings.reduce((sum, b) => {
    // Use actual annual_load_kwh from building metadata when available
    const realLoad = b.metadata?.annual_load_kwh
    if (realLoad && realLoad > 0) return sum + realLoad

    // Fallback to country-specific defaults by building type
    const units = b.metadata?.num_units || b.num_units || 1
    const base = b.building_type === 'commercial' ? countryRef.commercialKwh
      : b.building_type === 'municipal' ? countryRef.municipalKwh
      : b.building_type === 'industrial' ? countryRef.commercialKwh
      : countryRef.residentialKwhPerUnit * units
    const assetBoost = (b.building_assets || []).reduce((s, a) => {
      if (a.asset_type === 'heat_pump') return s + 3500
      if (a.asset_type === 'ev_charger') return s + 2500
      return s
    }, 0)
    return sum + base + assetBoost
  }, 0)

  // PV capacity
  const communityPvKwp = assets
    .filter(a => ['solar_pv_farm', 'ground_mounted_pv', 'pv'].includes(a.asset_type))
    .reduce((sum, a) => sum + (a.specs?.capacity_kwp || 0), 0)
  const buildingPvKwp = buildings.reduce((sum, b) =>
    sum + (b.building_assets || [])
      .filter(a => a.asset_type === 'rooftop_pv')
      .reduce((s, a) => s + (a.specs?.capacity_kwp || 0), 0)
  , 0)
  const totalPvKwp = communityPvKwp + buildingPvKwp

  // Annual PV generation — country-aware yield (kWh/kWp varies with latitude/irradiance)
  const annualPvKwh = totalPvKwp * countryRef.pvYieldKwhPerKwp

  // Battery capacity (both community-level and building-level)
  const batteryKwh = assets
    .filter(a => ['community_battery', 'battery'].includes(a.asset_type))
    .reduce((sum, a) => sum + (a.specs?.capacity_kwh || 0), 0)
    + buildings.reduce((sum, b) =>
      sum + (b.building_assets || [])
        .filter(a => a.asset_type === 'battery')
        .reduce((s, a) => s + (a.specs?.capacity_kwh || 0), 0)
    , 0)

  // Compute SCR & SSR (parametric baseline model, WITHOUT smart optimization)
  // Uses same IEA PVPS empirical model as backend _compute_scr() in kpi_engine.py.
  // SCR DECREASES with PV/load ratio: small PV → nearly all absorbed; large PV → surplus exported.
  const pvLoadRatio = annualPvKwh / (totalAnnualKwh || 1)
  const dailyPvKwh = annualPvKwh / 365

  // Base SCR (no battery): power-law fit to IEA PVPS data
  const baseSCR = pvLoadRatio < 0.01
    ? 0.97
    : Math.min(0.97, Math.max(0.20, 0.35 * Math.pow(pvLoadRatio, -0.60)))

  // Battery contribution: shifts surplus to evening load
  let effectiveSCR = baseSCR
  if (batteryKwh > 0) {
    const dailySurplus = Math.max(0, dailyPvKwh * (1.0 - baseSCR))
    if (dailySurplus > 0) {
      const coverage = Math.min(batteryKwh / dailySurplus, 1.0)
      const batteryIncrease = (1.0 - baseSCR) * coverage * 0.60
      effectiveSCR = Math.min(0.95, baseSCR + batteryIncrease)
    }
  }

  const scr = Math.min(98, Math.round(effectiveSCR * 100))
  const selfConsumedKwh = Math.min(annualPvKwh * (scr / 100), totalAnnualKwh)
  const ssr = Math.min(95, Math.round((selfConsumedKwh / (totalAnnualKwh || 1)) * 100))

  // Cost estimates — use community tariff metadata if available, else country default
  const meta = community.metadata || {}
  const avgTariff = meta.import_tariff
    ? (isCZK ? meta.import_tariff : meta.import_tariff)
    : (isCZK ? countryRef.avgTariff : countryRef.avgTariff)
  const gridImportKwh = Math.max(0, totalAnnualKwh - selfConsumedKwh)
  const dailyCost = +((gridImportKwh / 365) * avgTariff).toFixed(1)
  const yearlyCost = Math.round(gridImportKwh * avgTariff)

  return {
    selfConsumption: scr,
    selfSufficiency: ssr,
    dailyCost,
    yearlyCost,
    currency,
    totalAnnualKwh,
    totalPvKwp,
    batteryKwh,
    annualPvKwh,
    gridImportKwh: Math.round(gridImportKwh),
    countryRef,  // Expose for use by dashboard charts
  }
}
